import { BRANDING_DEFAULT, type Branding, tenantConfigSchema } from "@copi/shared";

import { API_URL } from "@/lib/api/client";

/**
 * Resolución del branding por subdominio (ADR 040 — Fase 1 del SaaS).
 *
 * Es la implementación **async** de `BrandingSource`: reemplaza la constante de
 * Fase 0 sin que ningún consumidor cambie (esa era la costura del ADR 039).
 */

/**
 * Deriva el slug de institución del hostname.
 *  - `unheval.copi.app` → "unheval"
 *  - `localhost`, `127.0.0.1`, IPs, o dominio plano (`copi.app`) → "" (default).
 *
 * Heurística conservadora: sólo trata el primer label como slug cuando hay al
 * menos 3 labels (sub.dominio.tld) y no es `www`. Evita tomar "copi" de
 * `copi.app` como tenant.
 */
export function slugDesdeHostname(hostname: string): string {
  // Hosts sin subdominio real (desarrollo / acceso directo).
  if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return "";

  const labels = hostname.split(".");
  if (labels.length < 3) return ""; // dominio plano: copi.app
  const primero = labels[0];
  if (!primero || primero === "www") return "";
  return primero;
}

/**
 * Pide el branding al backend para un slug. Falla cerrado: cualquier error de red
 * o validación degrada al branding por defecto, de modo que la app nunca queda
 * sin estilos (el backend ya degrada por slug inexistente; esto cubre el resto).
 */
export async function fetchTenantConfig(slug: string): Promise<Branding> {
  try {
    const url = new URL(`${API_URL}/tenant-config`);
    if (slug) url.searchParams.set("slug", slug);
    const res = await fetch(url.toString(), { credentials: "omit" });
    if (!res.ok) return BRANDING_DEFAULT;
    const body: unknown = await res.json();
    // El backend envuelve en `{ data }` (ResponseInterceptor).
    const data = (body as { data?: unknown }).data ?? body;
    const parsed = tenantConfigSchema.safeParse(data);
    return parsed.success ? parsed.data.branding : BRANDING_DEFAULT;
  } catch {
    return BRANDING_DEFAULT;
  }
}

/**
 * Resuelve el slug del tenant para la ventana actual.
 *  - Override de desarrollo: `?tenant=<slug>` en la URL (sólo fuera de producción).
 *    En localhost no hay subdominios reales, así que este query param permite
 *    previsualizar cualquier institución (ej. `?tenant=udh`). Persiste en
 *    sessionStorage para que sobreviva a la navegación interna de la SPA.
 *  - Producción: el subdominio del hostname (`udh.copi.app` → "udh").
 */
export function resolverSlugTenant(): string {
  if (import.meta.env.DEV) {
    const param = new URLSearchParams(window.location.search).get("tenant");
    if (param !== null) {
      // "" limpia el override (vuelve al default); un slug lo fija.
      if (param) sessionStorage.setItem("dev_tenant", param);
      else sessionStorage.removeItem("dev_tenant");
    }
    const guardado = sessionStorage.getItem("dev_tenant");
    if (guardado) return guardado;
  }
  return slugDesdeHostname(window.location.hostname);
}

/** Fuente async de Fase 1: resuelve el branding del tenant actual. */
export async function tenantSourceFase1(): Promise<Branding> {
  return fetchTenantConfig(resolverSlugTenant());
}
